import { useState } from "react";
import { Button } from "@shadcn/components/ui/button";
import { Loader2, CheckCircle2 } from "lucide-react";
import { enquiryApi } from "@/data/enquiries/api";
import type { FinalSignupStepSchema } from "@schema-types/types/JourneySchema";

type ThemeWindow = {
  __THEME__?: {
    organisationName?: string;
    logoUrl?: string;
    primaryColor?: string;
    portalsEnabled?: boolean;
  };
};

const benefitLines = [
  "See updates on your case in one place, whenever it suits you.",
  "Secure messages and documents — no need to chase via email or phone.",
  "The fastest way to stay informed as your case moves forward.",
];

export const FinalSignupStep = ({
  title,
  description,
  enquiryId,
  readOnly,
}: Pick<FinalSignupStepSchema, "title" | "description"> & { enquiryId?: string; readOnly?: boolean }) => {
  const [portalBusy, setPortalBusy] = useState(false);
  const theme = typeof window !== "undefined" ? ((window as unknown as ThemeWindow).__THEME__ ?? {}) : {};
  const portalsEnabled = theme.portalsEnabled === true;
  const showPortalCta = portalsEnabled && !readOnly;
  const orgName = theme.organisationName?.trim() || "your legal team";
  const heading = title?.trim() || "Thank you — your information is in safe hands";
  const customDescription = description?.trim();
  const defaultAcknowledgment =
    "We have received your details. We will begin work on your case and will be in touch with the next steps.";
  const defaultPortalPrompt =
    "Create your secure client portal account or sign in to link this enquiry to your existing account.";

  const onPortalSignup = async () => {
    if (!enquiryId || portalBusy) return;
    setPortalBusy(true);
    try {
      const { redirectUrl } = await enquiryApi.createPortalInvite(enquiryId);
      if (redirectUrl) window.location.assign(redirectUrl);
    } finally {
      setPortalBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col items-center text-center">
        {theme.logoUrl ? (
          <img src={theme.logoUrl} alt="" className="mb-6 h-12 max-w-[200px] object-contain" />
        ) : null}
        <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">{heading}</h1>
        {customDescription ? (
          <p className="mt-4 max-w-xl text-base text-muted-foreground">{customDescription}</p>
        ) : (
          <div className="mt-4 max-w-xl space-y-3 text-base text-muted-foreground">
            <p>{defaultAcknowledgment}</p>
            <p>{defaultPortalPrompt}</p>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-border bg-muted/30 p-6 sm:p-8">
        <p className="mb-4 text-center text-sm font-medium text-foreground">Why use the client portal?</p>
        <ul className="mx-auto max-w-lg space-y-3 text-left text-sm text-muted-foreground">
          {benefitLines.map((line) => (
            <li key={line} className="flex gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
              <span>{line}</span>
            </li>
          ))}
        </ul>
        {showPortalCta ? (
          <div className="mt-8 flex flex-col items-center gap-2">
            <Button type="button" variant="default" size="lg" disabled={portalBusy || !enquiryId} onClick={onPortalSignup}>
              {portalBusy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Please wait
                </>
              ) : (
                "Continue to client portal"
              )}
            </Button>
            {!enquiryId ? (
              <p className="text-xs text-muted-foreground">Your session is still loading — try again in a moment.</p>
            ) : null}
          </div>
        ) : (
          <p className="mt-6 text-center text-sm text-muted-foreground">
            {readOnly
              ? "Preview only — portal signup is not available here."
              : `${orgName} will be in touch with your next steps.`}
          </p>
        )}
      </div>
    </div>
  );
};
